import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button, Chip, TextField, LinearProgress, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, IconButton, InputAdornment
} from '@mui/material';
import {
  getDedicatedServers, createDedicated, deleteDedicated, getUpgradeOptions, upgradeDedicated,
  getDedicatedUsers, assignDedicated, unassignDedicated, setDedicatedConcurrency,
  getDedicatedServerTypes, adoptDedicated, setDedicatedMode
} from '../api';

const statusColor = (s) => s === 'active' ? 'success' : s === 'provisioning' ? 'warning' : s === 'failed' ? 'error' : 'default';
// палітра для нових серверів (продовжує кольори наявних: фіол/помаранч/зелен/бірюза)
const PALETTE = ['#e91e63', '#ffc107', '#00e676', '#2196f3', '#ff5722', '#9c27b0', '#26c6da', '#8bc34a', '#ff4081', '#3f51b5'];
const accentFor = (i) => PALETTE[i % PALETTE.length];
const barColor = (v) => v == null ? '#666' : v > 85 ? '#f44336' : v > 70 ? '#ff9800' : v > 50 ? '#ffeb3b' : '#4caf50';

const MetricBar = ({ label, value, icon }) => (
  <Box sx={{ mb: 1 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
      <Typography variant="caption" sx={{ color: '#aaa' }}>{icon} {label}</Typography>
      <Typography variant="caption" sx={{ color: barColor(value), fontWeight: 'bold' }}>
        {value != null ? value.toFixed(1) + '%' : '—'}
      </Typography>
    </Box>
    <LinearProgress variant="determinate" value={value || 0}
      sx={{ height: 6, borderRadius: 3, bgcolor: '#333', '& .MuiLinearProgress-bar': { bgcolor: barColor(value), borderRadius: 3 } }} />
  </Box>
);

const InfoRow = ({ label, value }) => value != null ? (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
    <Typography variant="caption" sx={{ color: '#888' }}>{label}</Typography>
    <Typography variant="caption" sx={{ color: '#ddd' }}>{value}</Typography>
  </Box>
) : null;

// section: 'controls' — компактна панель створення (в ряд зі статистикою); 'cards' — картки серверів
export default function DedicatedServers({ section = 'all' }) {
  const [servers, setServers] = useState([]);
  const [users, setUsers] = useState([]);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [assignFor, setAssignFor] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [types, setTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [chosenType, setChosenType] = useState('');
  const [adoptOpen, setAdoptOpen] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [adName, setAdName] = useState('');
  const [adIp, setAdIp] = useState('');
  const [adSecret, setAdSecret] = useState('');
  const [adConc, setAdConc] = useState(2);
  const [upgOpen, setUpgOpen] = useState(false);
  const [upgSrv, setUpgSrv] = useState(null);
  const [upgData, setUpgData] = useState(null);
  const [upgLoading, setUpgLoading] = useState(false);
  const [upgChosen, setUpgChosen] = useState('');

  const showControls = section === 'all' || section === 'controls';
  const showCards = section === 'all' || section === 'cards';

  const load = () => {
    getDedicatedServers().then(r => setServers(r.data || [])).catch(() => {});
    getDedicatedUsers().then(r => setUsers(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); const i = setInterval(load, 8000); return () => clearInterval(i); }, []);

  const openCreate = () => {
    setCreateOpen(true); setNewName(''); setChosenType(''); setTypes([]); setTypesLoading(true);
    getDedicatedServerTypes().then(r => {
      const list = r.data || [];
      setTypes(list);
      if (list.length) setChosenType(list[0].name);
    }).catch(() => setTypes([])).finally(() => setTypesLoading(false));
  };
  const doCreate = () => {
    let nm = (newName || '').trim().toLowerCase();
    if (!nm) { const d = new Date(), p = n => String(n).padStart(2, '0'); nm = 'worker-' + String(d.getUTCFullYear()).slice(2) + p(d.getUTCMonth() + 1) + p(d.getUTCDate()) + p(d.getUTCHours()) + p(d.getUTCMinutes()); }
    if (!/^[a-z0-9][a-z0-9-]{2,30}$/.test(nm)) { alert('Назва: 3-31 символ, малі латинські/цифри/дефіс'); return; }
    if (!chosenType) { alert('Оберіть тип сервера'); return; }
    setBusy(true);
    createDedicated(nm, chosenType)
      .then(() => { setCreateOpen(false); setNewName(''); setTimeout(() => { load(); setBusy(false); }, 2500); })
      .catch(e => { setBusy(false); alert('Помилка: ' + (e.response?.data?.detail || e.message)); });
  };
  const del = (name) => {
    if (!window.confirm('Видалити сервер "' + name + '"? (Hetzner + відв\'язати юзерів)')) return;
    deleteDedicated(name).then(() => setTimeout(load, 1800)).catch(e => alert('Помилка: ' + e.message));
  };
  const doAdopt = () => {
    const nm = (adName || '').trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9-]{2,30}$/.test(nm)) { alert('Назва: 3-31 символ, малі латинські/цифри/дефіс'); return; }
    if (!/^(\d{1,3}\.){3}\d{1,3}$/.test((adIp || '').trim())) { alert('Некоректний IP'); return; }
    if (!adSecret) { alert('Вкажи root-пароль боксу'); return; }
    setBusy(true);
    adoptDedicated({ name: nm, ip: adIp.trim(), root_secret: adSecret, concurrency: adConc })
      .then(() => { setAdoptOpen(false); setAdName(''); setAdIp(''); setAdSecret(''); setTimeout(() => { load(); setBusy(false); }, 2500); })
      .catch(e => { setBusy(false); alert('Помилка: ' + (e.response?.data?.detail || e.message)); });
  };
  const openUpgrade = (s) => {
    setUpgSrv(s); setUpgOpen(true); setUpgData(null); setUpgChosen(''); setUpgLoading(true);
    getUpgradeOptions(s.stype || 'cpx32').then(r => setUpgData(r.data || {})).catch(() => setUpgData({ error: 'не вдалось отримати варіанти' })).finally(() => setUpgLoading(false));
  };
  const doUpgrade = () => {
    if (!upgChosen) { alert('Оберіть тип для апгрейду'); return; }
    if (!window.confirm('Змінити тип ' + upgSrv.name + ' -> ' + upgChosen.toUpperCase() + '? Диск лишиться той самий (можна повернути назад). Знімок-страховка, пауза ~1-2 хв.')) return;
    setBusy(true);
    upgradeDedicated(upgSrv.name, upgChosen)
      .then(() => { setUpgOpen(false); setTimeout(() => { load(); setBusy(false); }, 3000); })
      .catch(e => { setBusy(false); alert('Помилка: ' + (e.response?.data?.detail || e.message)); });
  };
  const assign = (uid, sname) => assignDedicated(uid, sname).then(load).catch(e => alert('Помилка: ' + (e.response?.data?.detail || e.message)));
  const unassign = (uid) => unassignDedicated(uid).then(load).catch(e => alert('Помилка: ' + e.message));
  const setMode = (name, val) => {
    setServers(ss => ss.map(s => s.name === name ? { ...s, mode: val } : s));
    setDedicatedMode(name, val).then(() => setTimeout(load, 1500)).catch(e => alert('Помилка: ' + (e.response?.data?.detail || e.message)));
  };
  const setConc = (name, val) => {
    setServers(ss => ss.map(s => s.name === name ? { ...s, concurrency: val } : s));  // оптимістично
    setDedicatedConcurrency(name, val).then(() => setTimeout(load, 1500)).catch(e => alert('Помилка: ' + (e.response?.data?.detail || e.message)));
  };

  return (
    <>
      {showControls && (
        <Box sx={{ ml: { md: 'auto' }, display: 'flex', gap: 1 }}>
          <Button variant="contained" size="small" onClick={openCreate} sx={{ bgcolor: 'var(--ac1, #7c4dff)', whiteSpace: 'nowrap' }}>
            🏭 Створити сервер
          </Button>
          <Button variant="outlined" size="small" onClick={() => setAdoptOpen(true)}
            sx={{ color: '#26c6da', borderColor: '#26c6da', whiteSpace: 'nowrap' }}>
            ➕ Додати наявний
          </Button>
        </Box>
      )}

      {showControls && (
        <Dialog open={adoptOpen} onClose={() => !busy && setAdoptOpen(false)} fullWidth maxWidth="sm"
          PaperProps={{ sx: { bgcolor: '#1a1a2e', color: '#fff' } }}>
          <DialogTitle sx={{ color: '#fff' }}>➕ Додати наявний сервер (Netcup/інший)</DialogTitle>
          <DialogContent dividers>
            <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1.5 }}>
              Замовлений вручну бокс (Ubuntu 22.04) → воркер через WireGuard-міст. Провізія ~10 хв.
              Root-пароль використовується один раз (кладемо ключ) і НЕ зберігається.
            </Typography>
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" onClick={() => setShowGuide(g => !g)} sx={{ color: '#26c6da', cursor: 'pointer', userSelect: 'none', fontWeight: 600 }}>
                📋 {showGuide ? '▲ Сховати інструкцію' : '▼ Як купити й підготувати сервер (покрокова інструкція)'}
              </Typography>
              {showGuide && (
                <Box sx={{ mt: 1, p: 1.5, bgcolor: 'rgba(255,255,255,0.04)', borderRadius: 1, fontSize: 12, color: '#bbb', lineHeight: 1.75 }}>
                  <div><b>1.</b> Купи сервер на <b>netcup.com</b> (напр. RS 1000 G12 — 4 ядра / 8GB / ~€9.87/міс).</div>
                  <div><b>2.</b> Зайди в <b>SCP</b> (servercontrolpanel.de) — окремий логін із вітального листа netcup.</div>
                  <div><b>3.</b> Вкладка <b>Media → Images</b> → обери <b>Ubuntu 22.04.5 UEFI amd64</b> → Install:</div>
                  <div style={{ paddingLeft: 14 }}>• Installation method: <b>Minimal</b></div>
                  <div style={{ paddingLeft: 14 }}>• Partitioning: <b>large (весь диск)</b></div>
                  <div style={{ paddingLeft: 14 }}>• Create additional user: <b>OFF</b></div>
                  <div style={{ paddingLeft: 14 }}>• <b>Send e-mail to me: ON</b> (щоб прийшов root-пароль)</div>
                  <div style={{ paddingLeft: 14 }}>• Confirm password: <b>пароль твого акаунта netcup</b> → <b>Install</b></div>
                  <div><b>4.</b> Скопіюй <b>root-пароль</b> (спливаюче вікно одразу після Install і/або лист на пошту).</div>
                  <div><b>5.</b> Вкладка <b>Network</b> → візьми <b>публічний IPv4</b> (частина <u>до</u> «/22»).</div>
                  <div><b>6.</b> Заповни поля нижче: <b>назва + IP + root-пароль</b> (👁 звір) + <b>concurrency 2</b> → Додати.</div>
                  <div style={{ marginTop: 6, color: '#e6a23c' }}>⚠️ ОС саме <b>Ubuntu 22.04</b> (не 24/26) — під неї збудований воркер. Провізія далі — автоматична (~10 хв).</div>
                </Box>
              )}
            </Box>
            <TextField fullWidth size="small" variant="outlined" label="Назва (напр. netcup-vip1)"
              value={adName} onChange={e => setAdName(e.target.value)} disabled={busy}
              sx={{ mb: 1.5, input: { color: '#fff' }, label: { color: '#aaa' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' } }} />
            <TextField fullWidth size="small" variant="outlined" label="Публічний IP боксу"
              value={adIp} onChange={e => setAdIp(e.target.value)} disabled={busy} placeholder="напр. 194.5.152.10"
              sx={{ mb: 1.5, input: { color: '#fff' }, label: { color: '#aaa' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' } }} />
            <TextField fullWidth size="small" type={showSecret ? 'text' : 'password'} variant="outlined" label="Root-пароль"
              value={adSecret} onChange={e => setAdSecret(e.target.value)} disabled={busy}
              InputProps={{ endAdornment: (<InputAdornment position="end"><IconButton onClick={() => setShowSecret(v => !v)} edge="end" size="small" sx={{ color: '#aaa' }} title="показати/сховати">{showSecret ? '🙈' : '👁'}</IconButton></InputAdornment>) }}
              sx={{ mb: 1.5, input: { color: '#fff' }, label: { color: '#aaa' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' } }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ color: '#888' }}>⚡ Concurrency:</Typography>
              {[2, 4].map(v => (
                <Box key={v} onClick={() => !busy && setAdConc(v)}
                  sx={{ cursor: 'pointer', px: 1.3, py: 0.3, borderRadius: 1.5, fontSize: 13, userSelect: 'none',
                        bgcolor: adConc === v ? '#26c6da' : 'rgba(255,255,255,0.06)', color: adConc === v ? '#000' : '#999',
                        fontWeight: adConc === v ? 'bold' : 'normal', border: `1px solid ${adConc === v ? '#26c6da' : 'transparent'}` }}>
                  {v}
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAdoptOpen(false)} disabled={busy} sx={{ color: '#aaa' }}>Скасувати</Button>
            <Button variant="contained" onClick={doAdopt} disabled={busy}
              sx={{ bgcolor: '#26c6da', color: '#000' }}>{busy ? 'Запускаю…' : '➕ Додати'}</Button>
          </DialogActions>
        </Dialog>
      )}

      {showControls && (
        <Dialog open={createOpen} onClose={() => !busy && setCreateOpen(false)} fullWidth maxWidth="sm"
          PaperProps={{ sx: { bgcolor: '#1a1a2e', color: '#fff' } }}>
          <DialogTitle sx={{ color: '#fff' }}>🏭 Новий дедикований сервер</DialogTitle>
          <DialogContent dividers>
            <TextField fullWidth size="small" variant="outlined" label="Назва сервера" placeholder="напр. client-vip1"
              value={newName} onChange={e => setNewName(e.target.value)} disabled={busy}
              sx={{ mb: 2, mt: 1, input: { color: '#fff' }, label: { color: '#aaa' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' } }} />
            <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mb: 1 }}>
              Оберіть тип — 3 найдешевші доступні зараз у Hetzner (сумісні з нашим образом):
            </Typography>
            {typesLoading ? (
              <Box sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinearProgress sx={{ flex: 1, bgcolor: '#333', '& .MuiLinearProgress-bar': { bgcolor: 'var(--ac1, #7c4dff)' } }} />
                <Typography variant="caption" sx={{ color: '#888' }}>перевіряю наявність…</Typography>
              </Box>
            ) : types.length === 0 ? (
              <Typography sx={{ color: '#f44336' }}>Немає доступних типів зараз (спробуй пізніше).</Typography>
            ) : types.map(ty => {
              const sel = chosenType === ty.name;
              return (
                <Box key={ty.name} onClick={() => !busy && setChosenType(ty.name)}
                  sx={{ cursor: 'pointer', p: 1.2, mb: 1, borderRadius: 2,
                        border: `2px solid ${sel ? 'var(--ac1, #7c4dff)' : '#333'}`,
                        bgcolor: sel ? 'rgba(var(--ac1rgb, 124, 77, 255),0.15)' : 'transparent',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                      {sel ? '🟣 ' : '⚪ '}{ty.name.toUpperCase()}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#aaa' }}>
                      {ty.cores} ядер · {ty.memory}GB RAM · {ty.disk}GB · {ty.arch}{ty.eur_per_core ? ` · ${ty.eur_per_core} €/ядро` : ''}
                    </Typography>
                    {(ty.vs || []).map((v, i) => (
                      <Typography key={i} variant="caption" sx={{ color: '#ffb74d', display: 'block' }}>↳ {v}</Typography>
                    ))}
                  </Box>
                  <Typography sx={{ color: '#4caf50', fontWeight: 700 }}>€{ty.price}/міс</Typography>
                </Box>
              );
            })}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)} disabled={busy} sx={{ color: '#aaa' }}>Скасувати</Button>
            <Button variant="contained" onClick={doCreate} disabled={busy || typesLoading || !chosenType}
              sx={{ bgcolor: 'var(--ac1, #7c4dff)' }}>{busy ? 'Створюю…' : '➕ Створити'}</Button>
          </DialogActions>
        </Dialog>
      )}

      {showCards && servers.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ color: '#aaa', mt: 3, mb: 1 }}>
            🏭 Дедиковані сервери (опт) — окрема черга на кожного призначеного юзера
          </Typography>
          <Grid container spacing={2}>
            {servers.map((s, i) => {
              const accent = accentFor(i);
              const provisioning = s.status === 'provisioning';
              return (
                <Grid item xs={12} md={3} key={s.id}>
                  <Card sx={{ bgcolor: '#1a1a2e', border: `1px solid ${accent}`, borderRadius: 2, height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ color: accent, fontWeight: 'bold' }}>🏭 {s.name}</Typography>
                          <Typography variant="caption" sx={{ color: '#666' }}>📡 {s.private_ip || '—'}</Typography>
                        </Box>
                        <Chip label={s.status} size="small" color={statusColor(s.status)} />
                      </Box>
                      <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mb: 1 }}>
                        🎬 Дедикований воркер · {(s.stype || 'cpx32').toUpperCase()} · <span style={{ color: '#4caf50' }}>💰 {s.price || '—'}</span>
                      </Typography>

                      {provisioning ? (
                        <Box sx={{ py: 2 }}>
                          <LinearProgress sx={{ bgcolor: '#333', '& .MuiLinearProgress-bar': { bgcolor: accent } }} />
                          <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 1 }}>Провізія ~4-5 хв…</Typography>
                        </Box>
                      ) : (
                        <>
                          <MetricBar label="CPU" value={s.cpu} icon="🖥" />
                          <MetricBar label="RAM" value={s.ram} icon="🧠" />
                          <MetricBar label="Диск" value={s.disk} icon="💾" />
                          {s.storage_pct != null && <MetricBar label="Відео (сховище)" value={s.storage_pct} icon="🎬" />}
                          <Divider sx={{ borderColor: '#333', my: 1 }} />
                          <InfoRow label="🔧 vCPU" value={s.cpu_cores ? s.cpu_cores + ' ядер' : null} />
                          <InfoRow label="🧠 RAM" value={s.ram_total_gb ? s.ram_total_gb + ' GB' : null} />
                          <InfoRow label="💾 Диск вільно" value={s.disk_free_gb ? s.disk_free_gb + ' / ' + s.disk_total_gb + ' GB' : null} />
                          {s.storage_gb != null && <InfoRow label="🎬 Відео (локально, 24г)" value={s.storage_gb + ' GB'} />}
                          <InfoRow label="⏱ Uptime" value={s.uptime_hours != null ? Math.floor(s.uptime_hours / 24) + 'д ' + Math.floor(s.uptime_hours % 24) + 'г' : null} />
                          <InfoRow label="📊 Load" value={s.load_avg} />
                        </>
                      )}

                      <Divider sx={{ borderColor: '#333', my: 1 }} />
                      <InfoRow label="📦 Черга" value={s.queue} />
                      <InfoRow label="👥 Юзерів" value={s.users} />

                      {/* Перемикач concurrency 2/4 */}
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: '#888' }}>⚡ Concurrency:</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {[2, 4].map(v => {
                            const active = s.concurrency === v;
                            return (
                              <Box key={v} onClick={() => !active && setConc(s.name, v)}
                                sx={{ cursor: active ? 'default' : 'pointer', px: 1.3, py: 0.3, borderRadius: 1.5, fontSize: 13, userSelect: 'none',
                                      bgcolor: active ? accent : 'rgba(255,255,255,0.06)', color: active ? '#000' : '#999',
                                      fontWeight: active ? 'bold' : 'normal', border: `1px solid ${active ? accent : 'transparent'}`,
                                      '&:hover': { bgcolor: active ? accent : 'rgba(255,255,255,0.12)' } }}>
                                {v}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>

                      {/* Режим воркера — стиль як на старих картках */}
                      <Box sx={{ mt: 1, bgcolor: 'rgba(0,0,0,0.25)', borderRadius: 2, px: 1.5, py: 0.8 }}>
                        <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>🎬 Режим воркера</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {[['off', '⛔ Вимк', '#f44336'], ['short', '📱 Короткі', '#ff9800'], ['all', '🎬 Всі', '#4caf50']].map(([m, label, col]) => {
                            const active = (s.mode || 'all') === m;
                            return (
                              <Box key={m} onClick={() => !active && setMode(s.name, m)}
                                sx={{ flex: 1, textAlign: 'center', cursor: active ? 'default' : 'pointer',
                                      px: 0.5, py: 0.5, borderRadius: 1.5, fontSize: 11, userSelect: 'none',
                                      bgcolor: active ? col : 'rgba(255,255,255,0.06)',
                                      color: active ? '#000' : '#999', fontWeight: active ? 'bold' : 'normal',
                                      border: `1px solid ${active ? col : 'transparent'}`,
                                      '&:hover': { bgcolor: active ? col : 'rgba(255,255,255,0.12)' } }}>
                                {label}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                        <Button size="small" variant="outlined" onClick={() => setAssignFor(s)} sx={{ color: accent, borderColor: accent }}>👥 Юзери</Button>
                        {!provisioning && <Button size="small" variant="outlined" onClick={() => openUpgrade(s)} sx={{ color: '#4caf50', borderColor: '#4caf50' }}>🔧 Тип</Button>}
                        <Button size="small" variant="outlined" color="error" onClick={() => del(s.name)}>🗑</Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {showCards && (
        <Dialog open={upgOpen} onClose={() => !busy && setUpgOpen(false)} fullWidth maxWidth="sm"
          PaperProps={{ sx: { bgcolor: '#1a1a2e', color: '#fff' } }}>
          <DialogTitle sx={{ color: '#fff' }}>🔧 Змінити тип · {upgSrv?.name}</DialogTitle>
          <DialogContent dividers>
            {upgLoading ? (
              <Box sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinearProgress sx={{ flex: 1, bgcolor: '#333', '& .MuiLinearProgress-bar': { bgcolor: '#4caf50' } }} />
                <Typography variant="caption" sx={{ color: '#888' }}>рахую варіанти…</Typography>
              </Box>
            ) : upgData?.error ? (
              <Typography sx={{ color: '#f44336' }}>{upgData.error}</Typography>
            ) : !upgData?.options?.length ? (
              <Typography sx={{ color: '#aaa' }}>Немає доступних більших типів зараз.</Typography>
            ) : (
              <>
                <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mb: 1 }}>
                  Зараз {upgData.current?.name?.toUpperCase()}: {upgData.current?.cores} ядер · комфортно ~{upgData.current?.comfy_users} юзерів
                </Typography>
                {upgData.options.map(o => {
                  const sel = upgChosen === o.name;
                  return (
                    <Box key={o.name} onClick={() => !busy && setUpgChosen(o.name)}
                      sx={{ cursor: 'pointer', p: 1.2, mb: 1, borderRadius: 2,
                            border: `2px solid ${sel ? '#4caf50' : '#333'}`,
                            bgcolor: sel ? 'rgba(76,175,80,0.15)' : 'transparent',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography sx={{ color: '#fff', fontWeight: 600 }}>{sel ? '🟢 ' : '⚪ '}{o.name.toUpperCase()}</Typography>
                        <Typography variant="caption" sx={{ color: '#aaa' }}>{o.cores} ядер ({o.d_cores >= 0 ? '+' : ''}{o.d_cores}) · {o.memory}GB RAM · {o.disk}GB</Typography>
                        <Typography variant="caption" sx={{ color: '#81c784', display: 'block' }}>~{o.comfy_users} юзерів комфортно ({o.add_users >= 0 ? '+' : ''}{o.add_users})</Typography>{o.cheaper && <Typography variant="caption" sx={{ color: '#4fc3f7', display: 'block' }}>↩ дешевше — можна повернути назад</Typography>}
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ color: '#4caf50', fontWeight: 700 }}>€{o.price}/міс</Typography>
                        <Typography variant="caption" sx={{ color: o.cheaper ? '#4fc3f7' : '#ffb74d', display: 'block' }}>{o.price_delta >= 0 ? ('+€' + o.price_delta) : ('−€' + (-o.price_delta))}</Typography>
                      </Box>
                    </Box>
                  );
                })}
                <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 1 }}>
                  Знімок-страховка → пауза ~1-2 хв → зміна розміру → перевірка → прибрати знімок.
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUpgOpen(false)} disabled={busy} sx={{ color: '#aaa' }}>Скасувати</Button>
            <Button variant="contained" onClick={doUpgrade} disabled={busy || upgLoading || !upgChosen} sx={{ bgcolor: '#4caf50' }}>{busy ? 'Змінюю…' : '🔧 Змінити тип'}</Button>
          </DialogActions>
        </Dialog>
      )}

      {showCards && (
        <Dialog open={!!assignFor} onClose={() => setAssignFor(null)} fullWidth maxWidth="sm"
          PaperProps={{ sx: { bgcolor: '#1a1a2e', color: '#fff' } }}>
          <DialogTitle sx={{ color: '#fff' }}>Призначити юзерів → {assignFor?.name}</DialogTitle>
          <DialogContent dividers>
            <List dense>
              {users.map(u => {
                const here = u.server_name === assignFor?.name;
                const elsewhere = u.server_name && !here;
                return (
                  <ListItem key={u.id} secondaryAction={
                    here
                      ? <Button size="small" color="error" onClick={() => unassign(u.id)}>Зняти</Button>
                      : <Button size="small" variant="outlined" onClick={() => assign(u.id, assignFor.name)}>Призначити</Button>
                  }>
                    <ListItemText
                      primaryTypographyProps={{ sx: { color: '#fff' } }}
                      secondaryTypographyProps={{ sx: { color: here ? '#4caf50' : '#888' } }}
                      primary={'#' + u.id + ' ' + (u.telegram_username || '(без імені)')}
                      secondary={here ? '✅ на цьому сервері' : elsewhere ? ('на ' + u.server_name) : 'спільний пул'}
                    />
                  </ListItem>
                );
              })}
            </List>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
